import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Loader2, ArrowLeft, Edit, Eye, Calendar, User, BookOpen, Share2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import oneSpaceApi from '../lib/onespace-api';

// Wiki item interface
interface WikiItem {
  id: string;
  title: string;
  slug: string;
  content?: string;
  excerpt?: string;
  team_id: string | number | null;
  team_name?: string;
  author_name?: string;
  created_at: string;
  updated_at: string;
  is_published: boolean;
  view_count?: number;
}

const WikiPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [wikiItem, setWikiItem] = useState<WikiItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch wiki content
  const fetchWikiContent = async () => {
    if (!id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      if (isAuthenticated) {
        const response = await oneSpaceApi.get(`/api/2wikicontent/${id}`);
        
        if (response.data && response.data.data) {
          setWikiItem(response.data.data);
        }
      } else {
        // Demo mode - find in demo data
        const demoItem = getDemoWikiItem(id);
        if (demoItem) {
          setWikiItem(demoItem);
        } else {
          setError('Wiki item niet gevonden.');
        }
      }
    } catch (err) {
      console.error('Error fetching wiki content:', err);
      setError('Kon wiki inhoud niet laden.');
    } finally {
      setIsLoading(false);
    }
  };

  // Load wiki content on mount
  useEffect(() => {
    fetchWikiContent();
  }, [id, isAuthenticated]);

  // Format full date
  const formatFullDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get demo wiki item by ID
  const getDemoWikiItem = (wikiId: string): WikiItem | null => {
    const demoItems: WikiItem[] = [
      {
        id: '1',
        title: 'Onboarding Proces Nieuwe Medewerkers',
        slug: 'onboarding-proces-nieuwe-medewerkers',
        content: 'Dit is een uitgebreide gids voor het inwerken van nieuwe teamleden. Het bevat alle stappen, documenten en processen die nodig zijn voor een succesvolle onboarding.\n\n## Stappen voor nieuwe medewerkers:\n\n1. **Welkomstgesprek** - Kennismaking met het team en de organisatie\n2. **Werkplek inrichting** - Laptop, accounts en toegangen regelen\n3. **Training programma** - Onderdelen die specifiek zijn voor de rol\n4. **Buddy systeem** - Koppeling met ervaren collega\n5. **Evaluatie na 30 dagen** - Terugkoppeling en aanpassingen\n\n## Belangrijke documenten:\n\n- Arbeidscontract en bijlagen\n- Handboek medewerker\n- IT beveiligingsrichtlijnen\n- Organisatiestructuur\n\n## Eerste week checklist:\n\n- [ ] Werkplek ingericht en IT getest\n- [ ] Introductie met alle teamleden\n- [ ] Toegang tot systemen en tools\n- [ ] Planning van eerste projecten\n- [ ] Afspraak met buddy ingepland\n\n## Veelgestelde vragen:\n\n**Q: Hoe lang duurt het onboarding proces?**\nA: Het volledige onboarding proces duurt ongeveer 3 maanden, met intensieve begeleiding in de eerste 30 dagen.\n\n**Q: Wie is mijn aanspreekpunt voor vragen?**\nA: Je direct leidinggevende en je toegewezen buddy zijn je primaire aanspreekpunten. Voor HR-gerelateerde vragen kun je altijd terecht bij het HR team.\n\n**Q: Wanneer krijg ik toegang tot alle systemen?**\nA: IT toegang wordt meestal binnen 24 uur na je eerste werkdag geregeld. Voor specifieke systemen kan dit langer duren afhankelijk van beveiligingsvereisten.\n\nVoor vragen kun je altijd terecht bij HR of je directe leidinggevende.',
        excerpt: 'Volledige gids voor het inwerken van nieuwe teamleden met alle benodigde stappen en documenten.',
        team_id: 1,
        team_name: 'HR Team',
        author_name: 'Sarah van der Berg',
        created_at: '2024-01-15T10:00:00Z',
        updated_at: '2024-01-20T14:30:00Z',
        is_published: true,
        view_count: 142
      },
      {
        id: '2',
        title: 'API Documentatie OneSpace',
        slug: 'api-documentatie-onespace',
        content: 'Technische documentatie voor alle OneSpace API endpoints inclusief authenticatie, rate limiting en response formats.\n\n## Authenticatie\n\nAlle API calls vereisen een bearer token:\n\n```\nAuthorization: Bearer your-api-token\n```\n\n## Base URL\n\n```\nhttps://api.onespace.com/v1\n```\n\n## Rate Limiting\n\nDe API heeft rate limiting van 1000 requests per uur per gebruiker. Headers in de response geven informatie over je huidige limiet:\n\n```\nX-RateLimit-Limit: 1000\nX-RateLimit-Remaining: 999\nX-RateLimit-Reset: 1640995200\n```\n\n## Endpoints\n\n### GET /api/wiki\nHaalt een lijst van wiki items op.\n\n**Parameters:**\n- `limit` (integer): Aantal items (default: 10, max: 100)\n- `published` (boolean): Filter op gepubliceerde status\n- `team_id` (integer): Filter op team ID\n- `search` (string): Zoekterm voor titel en content\n\n**Response:**\n```json\n{\n  "data": [\n    {\n      "id": "1",\n      "title": "Example Wiki",\n      "slug": "example-wiki",\n      "excerpt": "Short description...",\n      "team_id": 1,\n      "team_name": "Development",\n      "author_name": "John Doe",\n      "is_published": true,\n      "view_count": 42,\n      "created_at": "2024-01-01T00:00:00Z",\n      "updated_at": "2024-01-01T00:00:00Z"\n    }\n  ],\n  "meta": {\n    "total": 10,\n    "filtered": 5,\n    "limit": 5\n  }\n}\n```\n\n### POST /api/wiki\nMaakt een nieuw wiki item aan.\n\n**Payload:**\n```json\n{\n  "title": "string",\n  "content": "string",\n  "excerpt": "string (optional)",\n  "team_id": 1,\n  "is_published": true\n}\n```\n\n### GET /api/wiki/{id}\nHaalt een specifiek wiki item op inclusief volledige content.\n\n### PUT /api/wiki/{id}\nBijwerken van een bestaand wiki item.\n\n### DELETE /api/wiki/{id}\nVerwijdert een wiki item.\n\n## Error Handling\n\nDe API retourneert standaard HTTP status codes:\n\n- `200` - Success\n- `201` - Created\n- `400` - Bad Request\n- `401` - Unauthorized\n- `403` - Forbidden\n- `404` - Not Found\n- `422` - Validation Error\n- `500` - Internal Server Error\n\nError responses hebben deze structuur:\n\n```json\n{\n  "error": "Error message",\n  "message": "Detailed error description"\n}\n```',
        excerpt: 'Technische documentatie voor de OneSpace API endpoints met authenticatie en response voorbeelden.',
        team_id: 2,
        team_name: 'Development Team',
        author_name: 'Mike Johnson',
        created_at: '2024-01-10T09:15:00Z',
        updated_at: '2024-01-18T16:45:00Z',
        is_published: true,
        view_count: 256
      },
      {
        id: '3',
        title: 'Marketing Strategie Q1 2024',
        slug: 'marketing-strategie-q1-2024',
        content: 'Overzicht van marketing doelen en tactieken voor Q1 2024.\n\n## Hoofddoelen\n\n1. **Brand awareness verhogen** - 25% meer naamsbekendheid meten via brand tracking\n2. **Lead generatie** - 150 nieuwe kwalificeerde leads per maand\n3. **Customer retention** - Verhogen van 78% naar 85%\n4. **Market share** - 3% groei in ons primaire segment\n\n## Tactieken & Kanalen\n\n### Content Marketing\n- **Blog posts**: 2x per week, focus op thought leadership\n- **Video content**: 1x per week, product demos en klant cases\n- **Social media**: Dagelijks op LinkedIn, Twitter, Facebook\n- **Webinars**: 1x per maand, educatieve content voor prospects\n- **Whitepapers**: Kwartaal, diepgaande industry analyses\n\n### Paid Advertising\n- **Google Ads**: €2000/maand budget, focus op high-intent keywords\n- **LinkedIn Ads**: €1500/maand, targeting decision makers\n- **Facebook/Instagram**: €1000/maand, brand awareness campagnes\n- **Retargeting**: €500/maand, website bezoekers heractiveren\n\n### Events & Partnerships\n- **Vakbeurzen**: 2 grote events (TechConnect, BusinessExpo)\n- **Partnership deals**: 3 nieuwe strategische partners\n- **Speaking engagements**: Minimaal 4 conferenties\n- **Customer events**: Kwartaal klant meetup organiseren\n\n## KPI\'s & Targets\n\n### Traffic & Engagement\n- Website traffic: +30% (van 10k naar 13k unique visitors/maand)\n- Blog engagement: +25% gemiddelde tijd op pagina\n- Social media followers: +40% groei\n- Email open rate: >25% (current: 22%)\n\n### Conversie & Sales\n- Conversion rate: +15% (van 2.3% naar 2.6%)\n- Cost per lead: -20% (van €45 naar €36)\n- Sales qualified leads: 180 per maand\n- Customer lifetime value: +10%\n\n### Brand Metrics\n- Brand awareness: +25% in doelgroep\n- Net Promoter Score: >8.5\n- Share of voice: +15% in category discussions\n- Thought leadership: Top 3 industry ranking\n\n## Budget Allocatie\n\n**Totaal Q1 Budget: €25,000**\n\n- Paid advertising: €12,000 (48%)\n- Content creation: €6,000 (24%)\n- Events & partnerships: €4,000 (16%)\n- Tools & software: €2,000 (8%)\n- Overig/reserves: €1,000 (4%)\n\n## Timeline & Milestones\n\n### Januari\n- Campagne setup en content kalender\n- Nieuwe landing pages live\n- Partner agreements finaliseren\n- Q4 performance review en learnings\n\n### Februari\n- Alle paid campaigns live\n- Eerste webinar uitvoeren\n- Vakbeurs voorbereiding\n- Mid-quarter performance check\n\n### Maart\n- TechConnect vakbeurs deelname\n- Q1 performance evaluatie\n- Q2 planning voorbereiden\n- Customer satisfaction survey\n\n## Success Metrics\n\nKwartaal success wordt gemeten op:\n1. **Lead volume**: Minimaal 450 leads in Q1\n2. **Lead quality**: >60% marketing qualified leads\n3. **Pipeline impact**: €150k nieuwe pipeline value\n4. **Brand metrics**: Alle brand KPIs binnen target range\n\n## Risk Management\n\n**Belangrijkste risico\'s:**\n- Economic downturn affecting advertising spend\n- Key personnel changes in marketing team\n- Major algorithm changes affecting organic reach\n- Competitor aggressive pricing strategies\n\n**Mitigation strategies:**\n- Budget flexibility voor quick pivots\n- Cross-training team members\n- Diversified channel approach\n- Continuous competitive monitoring',
        excerpt: 'Overzicht van marketing doelen en tactieken voor Q1 2024 inclusief campagne planning.',
        team_id: 3,
        team_name: 'Marketing Team',
        author_name: 'Lisa Chen',
        created_at: '2024-01-05T11:20:00Z',
        updated_at: '2024-01-15T13:10:00Z',
        is_published: true,
        view_count: 89
      }
    ];

    return demoItems.find(item => item.id === wikiId) || null;
  };

  // Render wiki content with proper formatting
  const renderWikiContent = (content: string) => {
    return content
      .split('\n')
      .map((line, index) => {
        // Headers
        if (line.startsWith('### ')) {
          return (
            <h4 key={index} className="text-lg font-semibold text-gray-800 dark:text-white mt-8 mb-4">
              {line.replace('### ', '')}
            </h4>
          );
        }
        if (line.startsWith('## ')) {
          return (
            <h3 key={index} className="text-xl font-bold text-gray-800 dark:text-white mt-10 mb-6">
              {line.replace('## ', '')}
            </h3>
          );
        }
        if (line.startsWith('# ')) {
          return (
            <h2 key={index} className="text-2xl font-bold text-gray-800 dark:text-white mt-12 mb-8">
              {line.replace('# ', '')}
            </h2>
          );
        }
        
        // Bold text
        if (line.includes('**')) {
          const parts = line.split('**');
          return (
            <p key={index} className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
              {parts.map((part, i) => 
                i % 2 === 1 ? <strong key={i} className="font-semibold text-gray-900 dark:text-white">{part}</strong> : part
              )}
            </p>
          );
        }
        
        // Code blocks
        if (line.startsWith('```')) {
          return <div key={index} className="my-2" />; // Skip code block markers
        }
        if (line.startsWith('- `') || line.startsWith('  - `')) {
          const cleanLine = line.replace(/^(\s*)- `(.+)`(.*)/, '$1• $2$3');
          return (
            <li key={index} className="text-gray-700 dark:text-gray-300 ml-6 mb-2 font-mono text-sm">
              {cleanLine.replace(/^(\s*)•\s/, '')}
            </li>
          );
        }
        
        // Checkboxes
        if (line.includes('- [ ]') || line.includes('- [x]')) {
          const isChecked = line.includes('- [x]');
          const text = line.replace(/- \[[ x]\]\s/, '');
          return (
            <div key={index} className="flex items-center gap-2 mb-2 ml-4">
              <input
                type="checkbox"
                checked={isChecked}
                readOnly
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className={`text-gray-700 dark:text-gray-300 ${isChecked ? 'line-through opacity-60' : ''}`}>
                {text}
              </span>
            </div>
          );
        }
        
        // Q&A formatting
        if (line.startsWith('**Q:')) {
          return (
            <div key={index} className="mt-6 mb-2 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-l-4 border-blue-500">
              <p className="font-semibold text-blue-900 dark:text-blue-100">
                {line.replace(/\*\*/g, '')}
              </p>
            </div>
          );
        }
        if (line.startsWith('A:')) {
          return (
            <div key={index} className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border-l-4 border-green-500">
              <p className="text-green-900 dark:text-green-100">
                {line.replace('A: ', '')}
              </p>
            </div>
          );
        }
        
        // Lists
        if (line.startsWith('- ')) {
          return (
            <li key={index} className="text-gray-700 dark:text-gray-300 ml-6 mb-2 list-disc">
              {line.replace('- ', '')}
            </li>
          );
        }
        if (/^\d+\.\s/.test(line)) {
          return (
            <li key={index} className="text-gray-700 dark:text-gray-300 ml-6 mb-2 list-decimal">
              {line.replace(/^\d+\.\s/, '')}
            </li>
          );
        }
        
        // Empty lines
        if (line.trim() === '') {
          return <div key={index} className="h-4" />;
        }
        
        // Regular paragraphs
        return (
          <p key={index} className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
            {line}
          </p>
        );
      });
  };

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  const handleShare = () => {
    if (navigator.share && wikiItem) {
      navigator.share({
        title: wikiItem.title,
        text: wikiItem.excerpt,
        url: window.location.href
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link gekopieerd naar clipboard!');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Wiki laden...</p>
        </div>
      </div>
    );
  }

  if (error || !wikiItem) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
            Wiki niet gevonden
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error || 'De wiki pagina die je zoekt bestaat niet.'}
          </p>
          <Button onClick={handleBack} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Terug
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={handleBack} variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Terug
              </Button>
              
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {wikiItem.team_name || 'Wiki'}
                </span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button onClick={handleShare} variant="ghost" size="sm">
                <Share2 className="h-4 w-4 mr-2" />
                Delen
              </Button>
              
              <Button
                onClick={() => alert('Bewerken - functionaliteit wordt binnenkort toegevoegd')}
                variant="outline"
                size="sm"
              >
                <Edit className="h-4 w-4 mr-2" />
                Bewerken
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Title and Meta */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            {wikiItem.title}
          </h1>
          
          <div className="flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400">
            {wikiItem.author_name && (
              <div className="flex items-center gap-1">
                <User className="h-4 w-4" />
                <span>Door {wikiItem.author_name}</span>
              </div>
            )}
            
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>Bijgewerkt op {formatFullDate(wikiItem.updated_at)}</span>
            </div>
            
            {wikiItem.view_count && (
              <div className="flex items-center gap-1">
                <Eye className="h-4 w-4" />
                <span>{wikiItem.view_count} weergaven</span>
              </div>
            )}
          </div>
        </div>

        {/* Article Content */}
        <article className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8">
          <div className="prose dark:prose-invert max-w-none">
            {wikiItem.content ? (
              <div className="space-y-2">
                {renderWikiContent(wikiItem.content)}
              </div>
            ) : (
              <p className="text-gray-600 dark:text-gray-400 italic">
                Geen inhoud beschikbaar voor deze wiki pagina.
              </p>
            )}
          </div>
        </article>

        {/* Footer Actions */}
        <div className="mt-8 flex items-center justify-between">
          <Button onClick={handleBack} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Terug naar overzicht
          </Button>
          
          <div className="flex items-center gap-2">
            <Button onClick={handleShare} variant="ghost">
              <Share2 className="h-4 w-4 mr-2" />
              Delen
            </Button>
            
            <Button
              onClick={() => alert('Bewerken - functionaliteit wordt binnenkort toegevoegd')}
              variant="outline"
            >
              <Edit className="h-4 w-4 mr-2" />
              Bewerken
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default WikiPage; 